import "./list-transaction.scss";

import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Sidebar } from "primereact/sidebar";
import { CiSearch } from "react-icons/ci";
import { MdClear } from "react-icons/md";

import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import ErrorSection from "@shared/ui/error-section/error-section";
import SearchInput from "@shared/ui/search-input/search-input";
import HeaderContent from "@shared/ui/header-content/header-content";
import { useQyGetTransaction } from "@factory/query/transaction.query";
import { GetTransactionModel } from "@factory/model/query-model-transaction";
import { TableSort } from "@shared/models/table-sort";
import { FilterTransaction } from "../filter-transaction/filter-transaction";
import { useFilterTransactionContext } from "../filter-transaction/filter-transaction.context";
import TextBlock from "@shared/ui/text-block/text-block";
import { GetTransactionDto } from "@api/api";
import { currencyFormat } from "@shared/utility/formats/currency-format";
import DotLabel from "@shared/ui/dot-label/dot-label";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import StatusTag from "@core/ui/status-tag/status-tag";
import PaidTag from "@core/ui/paid-tag/paid-tag";
import classNames from "classnames";
import { GoPersonFill } from "react-icons/go";
import { FaMobile } from "react-icons/fa";
import { useTransactionUtil } from "@core/utility/transaction.helper";
import { Accordion, AccordionTab } from "primereact/accordion";
import ServiceInfo from "@core/ui/service-info/service-info";
import { BookingEnum } from "@core/model/booking-enum";
import { Tooltip } from "primereact/tooltip";
import GridTransaction from "../grid-transaction/grid-transaction";
import { useTransactionHelperContext } from "../transaction-helper/transaction-helper.context";

export function ListTransaction() {
  // THIS IS THE LOCAL DECLARATION
  const { expiryTimer, timeLeft, combinedServices, setTimeLeft } =
    useTransactionHelperContext();
  const { getEndDateTime, getTimerClassName, sortTransactionsByExpiration } = useTransactionUtil();
  const { formatTimeLocal } = useDateTimeContext();
  const navigate = useNavigate();
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });
  const [sort, setSort] = useState({});
  const [filterPanel, setFilterPanel] = useState(false);
  const { filterChipsElement, statusFilterElement, getFilterEntityValues } =
    useFilterTransactionContext();
  const [layout] = useState<
    "grid" | "list" | (string & Record<string, unknown>)
  >("grid");
  const [transactionData, setTransactionData] = useState<GetTransactionDto[]>(
    []
  );

  // THIS IS THE QUERY DECLARATION
  const {
    data: transactionResponse,
    isLoading,
    isError,
    error,
  } = useQyGetTransaction(
    searchTerm,
    rowLimit,
    pageNumber,
    sort,
    getFilterEntityValues(),
    undefined,
    true,
    (response) => {
      const data = response?.data || [];
      if (!tableSort.sortField) {
        setTransactionData(sortTransactionsByExpiration(data));
      } else {
        setTransactionData(data);
      }
    }
  );

  useEffect(() => {
    const interval = setInterval(() => {
      const _timeLeft = expiryTimer(transactionData);
      setTimeLeft(_timeLeft);
    }, 1000);

    return () => clearInterval(interval); // Cleanup on unmount
  }, [transactionData, expiryTimer, setTimeLeft]);

  // THIS IS THE LOCAL DECLARATION
  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    setSort({ [e.sortField]: order });
  };
  const navigateToRecord = (item: GetTransactionModel) => {
    navigate(`${item.code}`);
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handleNew = () => {
    navigate(`new`);
  };
  const handleScan = () => {
    navigate(`scan`);
  };

  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const filterElement = (
    <section>
      <div className="flex gap-4 items-center mb-2">
        <SearchInput
          clearIcon={<MdClear />}
          searchIcon={<CiSearch />}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search items"
          className="w-full block"
        />
        <Button
          label="Filter"
          severity="secondary"
          badgeClassName="p-badge-danger"
          onClick={() => setFilterPanel(true)}
          outlined
        />
      </div>

      {filterChipsElement}
      <Sidebar visible={filterPanel} onHide={() => setFilterPanel(false)}>
        <FilterTransaction />
      </Sidebar>
    </section>
  );
  const addonCol = (cell: GetTransactionDto) => (
    <div className="flex flex-col flex-wrap border rounded-lg border-dashed p-2">
      {(cell.addon || [])?.length > 0 ? (
        cell.addon?.map((item) => (
          <section key={item.code}>
            {item.item_name}: {currencyFormat(item.amount || 0)} x{" "}
            {item.quantity}
          </section>
        ))
      ) : (
        <span>N/A</span>
      )}
    </div>
  );
  const serviceCol = (cell: GetTransactionDto) => {
    const services = cell.service || [];
    const groupedMembers = combinedServices(services);
    const displayTimer = cell.status === BookingEnum.Ongoing;

    const filteredMembers = searchTerm.trim()
      ? groupedMembers.filter((item) => {
          const cleanSearch = searchTerm.trim().toLowerCase();
          const childName = `${item.first_name ?? ""} ${item.last_name ?? ""}`.toLowerCase();
          const serviceName = (item.service_name ?? "").toLowerCase();
          const parentName = `${cell.parent_first_name ?? ""} ${cell.parent_last_name ?? ""}`.toLowerCase();
          const mobileNumber = (cell.mobile_number ?? "").toLowerCase();
          const transactionCode = (cell.code ?? "").toLowerCase();

          return (
            childName.includes(cleanSearch) ||
            serviceName.includes(cleanSearch) ||
            parentName.includes(cleanSearch) ||
            mobileNumber.includes(cleanSearch) ||
            transactionCode.includes(cleanSearch)
          );
        })
      : groupedMembers;

    return (
      <div className="flex gap-2 flex-wrap rounded-sm">
        <section className="flex justify-between w-full px-2">
          <TextBlock
            label="Parent"
            icon={<GoPersonFill />}
            value={cell.parent_first_name + " " + cell.parent_last_name}
          />
          <TextBlock
            label="Mobile"
            icon={<FaMobile />}
            value={cell.mobile_number}
          />
        </section>
        <Accordion multiple activeIndex={[]} className="w-full">
          {filteredMembers.map((item) => {
            const end = item ? getEndDateTime(item) : null;
            const endTime = end ? formatTimeLocal(end) : null;
            const expiryNotice = (item.member ? timeLeft[item.member] : null) || timeLeft[item.code] || "Calculating";

            return (
              <AccordionTab
                key={item.member}
                header={
                  <span className="flex justify-between items-center gap-2 w-full">
                    <span className="font-bold white-space-nowrap">
                      {item.first_name} {item.last_name}
                    </span>

                    <div className="flex gap-2">
                      {endTime && !item.is_open_time ? (
                        <TextBlock label="End" value={endTime} />
                      ) : (
                        <DotLabel label="Open Time" classBg="bg-green-500" />
                      )}
                      <section>
                        {displayTimer ? (
                          <div
                            className={classNames(
                              "countdown-timer font-bold p-1 rounded-md text-sm transition-all duration-300", // // Reduced padding from p-2 to p-1 and added transition
                              getTimerClassName(expiryNotice)
                            )}
                          >
                            {expiryNotice}
                          </div>
                        ) : null}
                      </section>
                    </div>
                  </span>
                }
              >
                <ServiceInfo model={item} />
              </AccordionTab>
            );
          })}
        </Accordion>
      </div>
    );
  };
  const list = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{transactionResponse?.count}</b>
      </h5>

      <DataTable
        value={transactionData}
        selectionMode="single"
        onSelectionChange={(e) => navigateToRecord(e.value)}
        onSort={handleTableSort}
        sortField={tableSort.sortField}
        sortOrder={tableSort.sortOrder}
      >
        <Column body={serviceCol} field="service" header="Service"></Column>
        <Column body={addonCol} field="addon" header="Addon"></Column>
        <Column
          body={(cell) => currencyFormat(cell.total, "PHP")}
          field="total"
          header="Total"
        ></Column>
        <Column
          body={(cell) => <PaidTag isPaid={cell.is_paid} />}
          field="is_paid"
          header="Is Paid"
          sortable
        ></Column>
        <Column
          body={(cell) => <StatusTag status={cell.status} />}
          field="status"
          header="Status"
          sortable
        ></Column>
      </DataTable>

      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={transactionResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );
  const dataDisplay = (
    <div>
      {layout === "list" ? (
        list
      ) : (
        <GridTransaction
          transactions={transactionData}
          search={searchTerm}
          rowLimit={rowLimit}
          first={first}
          totalRecords={transactionResponse?.count}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );

  return (
    <div id="ListTransaction" className="transaction">
      <HeaderContent title="Booking">
        <span className="flex gap-2">
          <Button label="New" onClick={handleNew} />
          <Button label="Quick Book" onClick={handleScan} outlined />
        </span>
      </HeaderContent>

      <div className="px-7 pt-4 pb-2 border-b border-gray-200">
        {statusFilterElement}
      </div>

      <Tooltip
        target=".countdown-timer"
        content="Time left until service expires"
      />

      <div className="p-7">
        {filterElement}
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && dataDisplay}
        <Outlet />
      </div>
    </div>
  );
}

export default ListTransaction;
